import { useTranslate } from '@refinedev/core';
import {
  Button,
  Checkbox,
  Input,
  InputNumber,
  Radio,
  Slider,
  Space,
  Typography,
} from 'antd';
import { embedUrlOf, type DraftField, type WorksheetDraft } from './model';

/**
 * E34 · the Builder's LIVE PHONE PREVIEW (Behavior: "a live phone preview renders the draft with the
 * app's own worksheet components — the 'Homework — Worksheet (form)' screen — updated on every edit").
 * The portal cannot run the phone's React Native components (build finding F-86), so this is the web
 * rendition of the SAME schema, laid out as the phone's form is modelled (Fill & sign, E1): the name,
 * section by section — instructions and the section's video above its fields —, a consent's Sign block,
 * and Submit (or "Done reading" when there is nothing to answer). Inert: nothing here is submitted.
 * A section's video plays in place — a Learn video or a YouTube link in YouTube's embed, a Vimeo link in
 * Vimeo's (`embedUrlOf`).
 */
const fieldPreview = (f: DraftField) => {
  switch (f.type) {
    case 'longText':
      return <Input.TextArea rows={2} disabled />;
    case 'number':
      return <InputNumber disabled style={{ width: '100%' }} min={f.min ?? undefined} max={f.max ?? undefined} />;
    case 'scale':
      return (
        <Slider
          disabled
          min={f.min ?? 0}
          max={f.max ?? 10}
          marks={{ [f.min ?? 0]: String(f.min ?? 0), [f.max ?? 10]: String(f.max ?? 10) }}
        />
      );
    case 'singleChoice':
      return (
        <Radio.Group disabled>
          <Space direction="vertical">
            {f.options.filter((o) => o.trim()).map((o) => (
              <Radio key={o} value={o}>
                {o}
              </Radio>
            ))}
          </Space>
        </Radio.Group>
      );
    case 'multiChoice':
      return (
        <Checkbox.Group disabled>
          <Space direction="vertical">
            {f.options.filter((o) => o.trim()).map((o) => (
              <Checkbox key={o} value={o}>
                {o}
              </Checkbox>
            ))}
          </Space>
        </Checkbox.Group>
      );
    case 'date':
      return <Input disabled placeholder="YYYY-MM-DD" />;
    case 'checkbox':
      return <Checkbox disabled />;
    default:
      return <Input disabled />;
  }
};

export const WorksheetPhonePreview = ({ draft }: { draft: WorksheetDraft }) => {
  const t = useTranslate();
  const hasFields = draft.sections.some((s) => s.fields.length > 0);
  return (
    <div
      data-testid="worksheet-preview"
      style={{
        margin: '0 auto',
        width: 300,
        maxWidth: '100%',
        border: '10px solid #2f3a34',
        borderRadius: 32,
        padding: '18px 14px',
        background: '#fbfaf7',
        maxHeight: 620,
        overflowY: 'auto',
      }}
    >
      <Typography.Title level={5} data-testid="worksheet-preview-name">
        {draft.name || t('worksheets.builder.untitled')}
      </Typography.Title>
      {draft.sections.map((s, i) => (
        <div key={s.sectionId} style={{ marginBottom: 18 }} data-testid={`worksheet-preview-section-${s.sectionId}`}>
          <Typography.Text type="secondary" style={{ fontSize: 11 }}>
            {t('worksheets.builder.previewSection', { n: i + 1, count: draft.sections.length })}
          </Typography.Text>
          {s.title ? <Typography.Title level={5} style={{ marginTop: 4 }}>{s.title}</Typography.Title> : null}
          {s.text ? (
            <Typography.Paragraph style={{ whiteSpace: 'pre-wrap' }}>{s.text}</Typography.Paragraph>
          ) : null}
          {s.video && embedUrlOf(s.video) ? (
            <div style={{ marginBottom: 8 }}>
              <iframe
                title={s.video.title || t('worksheets.builder.video.title')}
                src={embedUrlOf(s.video) as string}
                width="100%"
                height={150}
                style={{ border: 0, borderRadius: 8 }}
                allow="encrypted-media; picture-in-picture"
                allowFullScreen
                data-testid={`worksheet-preview-video-${s.sectionId}`}
              />
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {s.video.title}
              </Typography.Text>
            </div>
          ) : null}
          {s.fields.map((f) => (
            <div key={f.fieldId} style={{ marginBottom: 10 }} data-testid={`worksheet-preview-field-${f.fieldId}`}>
              <Typography.Text>
                {f.label || t('worksheets.builder.card.untitled')}
                {f.required ? ' *' : ''}
              </Typography.Text>
              <div style={{ marginTop: 4 }}>{fieldPreview(f)}</div>
            </div>
          ))}
        </div>
      ))}
      {draft.signatureRequired ? (
        <div style={{ marginBottom: 12 }} data-testid="worksheet-preview-sign">
          <Typography.Text strong>{t('worksheets.builder.previewSign')}</Typography.Text>
          <Input disabled placeholder={t('worksheets.builder.previewSignName')} style={{ marginTop: 4 }} />
          <Checkbox disabled style={{ marginTop: 4 }}>
            {t('worksheets.builder.previewAgree')}
          </Checkbox>
        </div>
      ) : null}
      <Button type="primary" block disabled data-testid="worksheet-preview-submit">
        {hasFields ? t('worksheets.builder.previewSubmit') : t('worksheets.builder.previewDoneReading')}
      </Button>
    </div>
  );
};
